import { useEffect, useRef, useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';

// Use the shared axios instance — it carries the Authorization: Bearer
// interceptor set up by AuthContext. A bare `import axios from 'axios'`
// here would send unauthenticated requests and hit 401.
import { api } from '../../services/api.js';

// Bumped from v1 → v2 when the assistant was renamed to Lisa. This
// forces returning users onto the new greeting instead of showing the
// stale "I'm here to help" intro from their localStorage cache.
const STORAGE_KEY = 'atlacis_support_chat_v2';
const OPEN_EVENT = 'atlacis:open-support';

const GREETING = {
  role: 'assistant',
  content:
    "Hi! I'm Lisa, your Atlacis AI assistant. Ask me anything about the platform, or I can open a support ticket for you.",
};

function loadMessages() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // fall through
  }
  return [GREETING];
}

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist across navigation.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // localStorage quota or disabled — silent
    }
  }, [messages]);

  // Autoscroll on new message or when the panel opens.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (open && inputRef.current) {
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Allow other components (Header "Help" menu item) to open the chat.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const userMsg = { role: 'user', content: trimmed };
    const historyForApi = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      // api.baseURL is already '/api/re' — don't double-prefix it.
      const { data } = await api.post('/support/chat', {
        message: trimmed,
        conversation_history: historyForApi,
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.response },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating bubble — visible on every authenticated page. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 h-[60px] w-[60px] rounded-full shadow-lg
                   flex items-center justify-center text-white
                   hover:scale-105 transition-transform focus:outline-none
                   focus:ring-2 focus:ring-accent/50"
        style={{
          background:
            'linear-gradient(135deg, #000000 0%, #EA580C 100%)',
        }}
        aria-label={open ? 'Close support chat' : 'Open support chat'}
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat panel. */}
      {open && (
        <div
          role="dialog"
          aria-label="Lisa — Atlacis AI assistant"
          className="fixed z-50 flex flex-col overflow-hidden bg-white shadow-2xl rounded-card
                     bottom-[96px] right-6 w-[380px] h-[500px] border border-border-card"
        >
          {/* Header */}
          <div className="h-14 shrink-0 bg-white border-b border-border-card flex items-center gap-3 px-4">
            <img
              src="/chat-avatar.png"
              alt=""
              className="h-9 w-9 rounded-full bg-accent/15 object-cover shrink-0"
              onError={(e) => {
                // Fallback: hide the <img> and let the rounded bg show.
                e.currentTarget.style.visibility = 'hidden';
              }}
            />
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-sm font-semibold text-text-primary truncate">
                Lisa
              </div>
              <div className="text-[11px] text-text-secondary">
                AI Assistant
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="h-8 w-8 rounded-btn flex items-center justify-center text-text-muted
                         hover:text-text-primary hover:bg-bg-subtle transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-bg-subtle"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className="max-w-[85%] rounded-btn px-3 py-2 text-sm leading-snug whitespace-pre-wrap"
                  style={
                    m.role === 'user'
                      ? { backgroundColor: '#EA580C', color: '#FFFFFF' }
                      : { backgroundColor: '#F5F5F5', color: '#333333' }
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div
                  className="max-w-[85%] rounded-btn px-3 py-2 text-sm leading-snug italic"
                  style={{ backgroundColor: '#F5F5F5', color: '#7C7F95' }}
                >
                  Typing…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border-card bg-white flex items-center gap-2 shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Ask a question…"
              className="flex-1 h-10 px-3 rounded-btn border border-border-input text-sm
                         text-text-primary placeholder:text-text-muted
                         focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30
                         disabled:opacity-60"
              disabled={sending}
            />
            <button
              type="button"
              onClick={send}
              disabled={!input.trim() || sending}
              className="h-10 w-10 rounded-btn bg-accent text-white flex items-center justify-center
                         disabled:opacity-40 hover:bg-accent-hover transition-colors"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Exported so Header's "Help" menu item can trigger the open without
// importing the component directly (no provider plumbing needed).
export function openSupportChat() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}
